import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError, apiJson } from '../../lib/api'
import { PendingButton } from '../../components/pending'
import styles from './StrikePeriodsSetting.module.css'

// D9.3: which periods the Strike Board offers is the subscription's own
// choice, so an ADMIN makes it here, in Reports, beside the board it changes.
// It reads the same settings the Client Setup tab does and saves with the
// same stale-edit guard (`before`): a change someone else saved first is
// shown, not overwritten. It moves no season start, report figure, wage or
// pay period. The server enforces the permission regardless.
interface Settings {
  strike_periods?: string[]
  strike_period_options?: { k: string; label: string }[]
  can_edit_strike_periods?: boolean
}

export function StrikePeriodsSetting({ onSaved }: { onSaved: () => void }) {
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<Settings | null>(null)
  const [choice, setChoice] = useState<string[]>([])
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const show = (t: Settings) => {
    setData(t)
    setChoice(t.strike_periods || [])
  }

  useEffect(() => {
    if (!open || data) return
    const u = userRef.current
    if (!u) return
    let live = true
    apiJson<Settings>(u, '/api/settings/tenant')
      .then((t) => live && show(t))
      .catch((e) => live && setError(e?.message || 'Could not load the Strike Board periods'))
    return () => {
      live = false
    }
  }, [open, data])

  const options = data?.strike_period_options || []
  const saved = data?.strike_periods || []
  const next = options.map((o) => o.k).filter((k) => choice.includes(k))
  const can = !!data?.can_edit_strike_periods
  const canSave = can && next.length > 0 && next.join() !== saved.join() && !busy
  const labels = (keys: string[]) => options.filter((o) => keys.includes(o.k)).map((o) => o.label).join(' and ')

  const save = async () => {
    const u = userRef.current
    if (!u || !canSave) return
    setBusy(true)
    setError('')
    setNote('')
    try {
      const t = await apiJson<Settings>(u, '/api/settings/tenant/strike-periods', { method: 'PUT', body: { periods: next, before: saved } })
      show(t)
      setNote(`The Strike Board offers ${labels(t.strike_periods || [])}.`)
      onSaved()
    } catch (e: any) {
      const b = e instanceof ApiError ? e.body : null
      if (b?.code === 'STALE' && b.tenant) {
        show(b.tenant)
        setError('Someone else changed the Strike Board periods since you opened this. This shows what is saved now.')
      } else {
        setError(e?.message || 'The Strike Board periods were not saved')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.wrap}>
      <button type="button" className={styles.toggle} aria-expanded={open} aria-controls="strike-periods-panel" onClick={() => setOpen((o) => !o)}>
        Strike Board periods
      </button>
      {open && (
        <div id="strike-periods-panel" className={styles.panel} role="group" aria-label="Strike Board periods">
          {!data && !error && <p className={styles.note}>Loading…</p>}
          {data && (
            <>
              <div className={styles.row}>
                {options.map((o) => (
                  <label key={o.k} className={styles.check}>
                    <input
                      type="checkbox"
                      checked={choice.includes(o.k)}
                      // At least one stays: a board with no period shows nothing.
                      disabled={!can || busy || (choice.includes(o.k) && choice.length === 1)}
                      onChange={(e) => setChoice((c) => (e.target.checked ? [...c, o.k] : c.filter((k) => k !== o.k)))}
                    />
                    {o.label}
                  </label>
                ))}
                {can && (
                  <PendingButton className={styles.save} disabled={!canSave} busy={busy} pending="Saving…" onClick={save}>
                    Save periods
                  </PendingButton>
                )}
              </div>
              <p className={styles.note}>
                The periods the Strike Board offers; the first kept is the one it opens on. This changes only what the board offers: it does not change the season
                start, any report figure, wages or pay periods.
                {!can && ' Only an administrator can change it.'}
              </p>
            </>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          {note && (
            <p className={styles.ok} role="status">
              {note}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
